<script lang="ts">
    import { getContext, onDestroy, tick } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { orderBy } from 'lodash-es'

    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { session, simpleFetch, groups as userGroups } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'

    import { tmpToken, tmpTokenRem } from 'wdom/services/stream'

    import type { DelegateData, MiscData, User, UserHandler } from './user'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import TokenAnchorProgress from './TokenAnchorProgress.svelte'
    import TokenProgress from './TokenProgress.svelte'

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)
    const mdata: Readable<MiscData> = sderived(userh.miscData, ($v) => $v)

    const ddata: Readable<DelegateData> = sderived(userh.delegateData, ($v) => $v)

    let delegOfIds = $derived($ddata && $ddata.user_delegate_of_mapping ? new Set(Object.keys($ddata.user_delegate_of_mapping)) : new Set())

    let modal: null | 'domain-owner' = $state(null)

    let ownerDomainList = $state('')
    let ownerError: string | undefined = $state(undefined)
    let ownerStatus: 'ready' | 'working' | 'error' | 'success' = $state('ready')
    let ownerDomainResult: any[] = $state([])
    let ownerZoneResult: any[] = $state([])
    // let ownerAllOk: boolean = null

    let domainOwnerTextareaElement: HTMLTextAreaElement | undefined = $state()

    function openOwnershipChange() {
        ownerStatus = 'ready'
        ownerDomainList = ''
        ownerError = undefined
        ownerDomainResult = []
        ownerZoneResult = []
        // ownerAllOk = null
        modal = 'domain-owner'
        tick().then(() => {
            if (domainOwnerTextareaElement) domainOwnerTextareaElement.focus()
        })
    }

    async function changeOwnership() {
        ownerStatus = 'working'

        try {
            const r = await simpleFetch('POST', `/json/wdom-user/1/user/${$user.id}/set-domain-ownership`, { domains: ownerDomainList })
            // ownerAllOk = r.data.all_ok
            ownerDomainResult = orderBy([...Object.entries(r.data.domains)], [(i) => i[0]], ['asc'])
            ownerZoneResult = orderBy([...Object.entries(r.data.zones)], [(i) => i[0]], ['asc'])

            ownerStatus = 'success'
            await userh.load()
        } catch (e: any) {
            ownerError = e.data && e.data.error ? e.data.error : 'Erreur de communication avec le backend.'
            ownerStatus = 'error'
        }
    }

    $effect(() => {
        if (modal !== null && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (modal == null && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
    onDestroy(() => {
        if (document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })
</script>

<div class="card">
    <div class="card-title text-primary">{$_('Miscellaneous')}</div>
    {#if $status == 'loading' || $loading}
        <div class="card-body">
            <LoadingPattern pattern={[60, 40, 35, 20, 40]} />
        </div>
    {:else if $userGroups.has('global-domain-admin')}
        <div class="card-body">
            <ul class="list-unstyled">
                <li><a use:sanchor href={spath(`/wdom-user/${$user.id}/import`)}>Importer un domaine du CIRA, OpenSRS ou WebNames.</a></li>
                {#if $mdata.domain_count > 0}
                    <li>
                        <a use:sanchor href={spath(`/domains?q=${$user.id}`)}>{$ngettext('List user’s domain', 'List user’s {n} domains', { n: $mdata.domain_count })}</a>
                        (<TokenAnchorProgress href="/json/domain/1/user/{$user.id}/domains/tsv">TSV</TokenAnchorProgress>,
                        <TokenAnchorProgress href="/json/domain/1/user/{$user.id}/domains/csv">CSV</TokenAnchorProgress>,
                        <a use:sanchor href={spath(`/domains/map?q=${$user.id}`)}>Map</a>).
                    </li>
                {:else}
                    <li>{$_('User has no domains.')}</li>
                {/if}
                {#if $mdata.delegate_of_domain_count}
                    <li><a use:sanchor href={spath(`/domains?q=${[...delegOfIds].join(' ')}`)}>{$ngettext('Être délégué donne accès à {n} domaine.', 'Être délégué donne accès à {n} domaines.', { n: $mdata.delegate_of_domain_count })}</a></li>
                {/if}
                {#if $mdata.near_expiration_count > 0}
                    <li><a use:sanchor class="text-danger" href={spath(`/domains?q=${$user.id}&s_by=expiration`)}>{$ngettext('Has {n} domain about to expire.', 'Has {n} domains about to expire.', { n: $mdata.near_expiration_count })}</a></li>
                {/if}
                <li>
                    <a use:sanchor href={spath(`/tasks?owner=${$user.id}`)}>{$_('View user’s tasks.')}</a>
                </li>
                <li>
                    <button type="button" class="btn btn-link btn-a" style="padding: 0; border: 0;" onclick={() => openOwnershipChange()}>Changer le propriétaire de domaines.</button>
                </li>
                <li>
                    <a use:sanchor href={spath(`/transfers?text=${$user.id}`)}>{$_('Afficher les transferts.')}</a>
                </li>
            </ul>
        </div>
    {/if}
</div>

{#if modal === 'domain-owner'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <form
                    class="form-horizontal"
                    onsubmit={(e) => {
                        e.preventDefault()
                        changeOwnership()
                    }}
                >
                    <div class="modal-header text-bg-primary">
                        <div>Changement de propriétaire de domaines</div>
                    </div>
                    {#if ownerStatus == 'ready' || ownerStatus == 'working'}
                        <div class="modal-body">
                            <div class="row">
                                <label for="domain-list" class="col-sm-4 col-form-label">Domaines</label>
                                <div class="col-sm-8">
                                    <p class="for-text">Lister les domaines et zones par nom (séparer par ligne ou espaces).</p>
                                    <textarea disabled={ownerStatus == 'working'} id="domain-list" class="form-control" bind:value={ownerDomainList} bind:this={domainOwnerTextareaElement}></textarea>
                                </div>
                            </div>
                        </div>
                    {:else if ownerStatus == 'success'}
                        <div class="modal-body">
                            {#if ownerDomainResult.length}
                                <p>Domaines (et zones associées) :</p>
                                <ul class="list-unstyled domain-change-ownership-result">
                                    {#each ownerDomainResult as [dname, dres]}
                                        <li>
                                            {#if dres.success}
                                                <span class:text-danger={!dres.success}>
                                                    <Icon inline icon={dres.success ? 'fa:check text-success' : 'fa:times'} />
                                                </span>
                                                <span>{dname} :</span>
                                                <span>Succès</span>
                                            {:else}
                                                <Icon inline icon="fa:times text-danger" />
                                                <span>{dname} :</span>
                                                <span>{dres.error}</span>
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            {/if}
                            {#if ownerZoneResult.length}
                                <p>Zones seules :</p>
                                <ul class="list-unstyled domain-change-ownership-result">
                                    {#each ownerZoneResult as [dname, dres]}
                                        <li>
                                            {#if dres.success}
                                                <span class:text-danger={!dres.success}>
                                                    <Icon inline icon={dres.success ? 'fa:check text-success' : 'fa:times'} />
                                                </span>
                                                <span>{dname} :</span>
                                                <span>Succès</span>
                                            {:else}
                                                <Icon inline icon="fa:times text-danger" />
                                                <span>{dname} :</span>
                                                <span>{dres.error}</span>
                                            {/if}
                                        </li>
                                    {/each}
                                </ul>
                            {/if}
                        </div>
                    {:else if ownerStatus == 'error'}
                        <div class="modal-body">
                            <p>Une erreur s’est produite :</p>
                            <p>{ownerError}</p>
                        </div>
                    {/if}
                    <div class="modal-footer text-bg-light">
                        {#if ownerStatus == 'ready' || ownerStatus == 'working'}
                            <button type="button" class="btn btn-outline-secondary" onclick={() => (modal = null)}>{$_('Cancel')}</button>
                            <button type="submit" class="btn btn-primary" disabled={ownerDomainList == ''}>{$_('Procéder')}</button>
                        {:else}
                            <button type="button" class="btn btn-outline-secondary" onclick={() => (modal = null)}>{$_('Close')}</button>
                        {/if}
                    </div>
                </form>
            </div>
        </div>
        <div
            class="modal-backdrop"
            aria-hidden="true"
            onclick={() => {
                modal = null
            }}
        ></div>
    </div>
{/if}
